// Maps a party between the create-contract form and the contract parties API.
//
// Everything is flat on the party. Saudi parties keep the payload they always had, plus the IBAN
// for individuals (it used to be dropped). International parties — phone outside KSA — also send
// `international: true` (the backend derives it from the phone anyway), `bankCountry`,
// `bankName`, and the optional `swiftCode`, `bankBranchName` and `bankDocument`. Their IBAN rides
// the same top-level `iban`, the account holder is the party's `sudoName`, and
// `companyUnifiedNumber` / `nationalAddress` carry free text for them.
import {
  IPartyBankDocument,
  isInternationalParty,
  isInternationalPhoneNumber,
  normalizeIban,
  toPartyPhoneNumber,
} from "../../../setup/util/partyCountry.helpers";

const trimmed = (value?: unknown) => `${value ?? ""}`.trim();

/** Identity, company, bank and address fields of a non-broker party, ready for the API. */
export const buildPartyDetailsPayload = (
  party: any
): Record<string, unknown> => {
  const phoneNumber = toPartyPhoneNumber(party?.phoneNumber);
  const iban = normalizeIban(party?.iban);

  return {
    phoneNumber,
    ...(party?.companyUnifiedNumber
      ? {
          companyUnifiedNumber: trimmed(party.companyUnifiedNumber),
          companyName: trimmed(party?.companyName),
        }
      : {}),
    ...(party?.nationalAddress
      ? { nationalAddress: trimmed(party.nationalAddress) }
      : {}),
    ...(iban ? { iban } : {}),
    ...(isInternationalPhoneNumber(phoneNumber)
      ? {
          international: true,
          bankCountry: trimmed(party?.bankCountry).toUpperCase(),
          bankName: trimmed(party?.bankName),
          ...(party?.swiftCode
            ? { swiftCode: trimmed(party.swiftCode).toUpperCase() }
            : {}),
          ...(party?.bankBranchName
            ? { bankBranchName: trimmed(party.bankBranchName) }
            : {}),
          ...(party?.bankDocument ? { bankDocument: party.bankDocument } : {}),
        }
      : {}),
  };
};

/** Form fields for a party read back from the API (company, IBAN, address, bank details). */
export const partyFormFieldsFromResponse = (
  party: any,
  fallbackCompanyName = ""
) => ({
  ...(party?.companyUnifiedNumber
    ? {
        isCompany: true,
        companyUnifiedNumber: party.companyUnifiedNumber || "",
        companyName: party?.companyName || fallbackCompanyName,
      }
    : {}),
  ...(party?.iban ? { iban: party.iban } : {}),
  ...(party?.nationalAddress ? { nationalAddress: party.nationalAddress } : {}),
  ...(isInternationalParty(party)
    ? {
        international: true,
        bankCountry: party?.bankCountry || "",
        bankName: party?.bankName || "",
        swiftCode: party?.swiftCode || "",
        bankBranchName: party?.bankBranchName || "",
        bankDocument: (party?.bankDocument as IPartyBankDocument) || null,
      }
    : {}),
});

/**
 * Messages from a rejected parties save (HTTP 400 `validationErrors`, keyed by milestone or
 * contract id). The portal is Arabic-only, so `messageAr` wins over the English `message`.
 */
export const getPartyValidationMessages = (error: any): string[] => {
  const validationErrors = error?.error?.validationErrors;
  if (!validationErrors || typeof validationErrors !== "object") return [];
  return Object.values(validationErrors)
    .flat()
    .map((err: any) => err?.messageAr || err?.message || "")
    .filter(Boolean);
};
